export type UrlValidationErrorCode = 
  | "required"
  | "invalid_https"
  | "invalid_format";

export interface UrlValidationResult {
  isValid: boolean;
  errorCode?: UrlValidationErrorCode;
}

export interface UrlValidationOptions {
  requireHttps?: boolean;
  allowEmpty?: boolean;
}

/**
 * Validates a URL string
 * @param url - The URL string to validate
 * @param options - Validation options
 * @returns UrlValidationResult with isValid boolean and optional error code
 */
export function validateUrl(
  url: string,
  options: UrlValidationOptions = {}
): UrlValidationResult {
  const { requireHttps = true, allowEmpty = false } = options;

  // Handle empty URLs
  if (!url || !url.trim()) {
    if (allowEmpty) {
      return { isValid: true };
    }
    return {
      isValid: false,
      errorCode: "required",
    };
  }

  const trimmedUrl = url.trim();

  // Check if URL starts with http:// or https://
  if (!trimmedUrl.match(/^https?:\/\//)) {
    return {
      isValid: false,
      errorCode: requireHttps ? "invalid_https" : "invalid_format",
    };
  }

  // Check if URL requires https://
  if (requireHttps && !trimmedUrl.startsWith("https://")) {
    return {
      isValid: false,
      errorCode: "invalid_https",
    };
  }

  // Validate URL format using URL constructor
  try {
    const urlObject = new URL(trimmedUrl);
    
    // Check if hostname exists and is valid
    if (!urlObject.hostname || urlObject.hostname.length < 3) {
      return {
        isValid: false,
        errorCode: "invalid_format",
      };
    }

    // Check if hostname has at least one dot (e.g., example.com)
    if (!urlObject.hostname.includes(".")) {
      return {
        isValid: false,
        errorCode: "invalid_format",
      };
    }

    return {
      isValid: true,
    };
  } catch {
    return {
      isValid: false,
      errorCode: "invalid_format",
    };
  }
}

/**
 * Normalizes a URL by prepending https:// if no protocol is present
 * @param url - The URL string to normalize
 * @returns Normalized URL string
 */
export function normalizeUrl(url: string): string {
  if (!url || !url.trim()) {
    return url;
  }

  const trimmedUrl = url.trim();

  // If already has protocol, return as is
  if (trimmedUrl.match(/^https?:\/\//)) {
    return trimmedUrl;
  }

  // Prepend https://
  return `https://${trimmedUrl}`;
}

/**
 * Checks if a URL is a valid HTTP/HTTPS URL
 * @param url - The URL string to check
 * @returns boolean indicating if URL is valid
 */
export function isValidUrl(url: string): boolean {
  return validateUrl(url, { allowEmpty: false }).isValid;
}

/**
 * Checks if a URL is a valid HTTPS URL
 * @param url - The URL string to check
 * @returns boolean indicating if URL is valid HTTPS
 */
export function isValidHttpsUrl(url: string): boolean {
  return validateUrl(url, { requireHttps: true, allowEmpty: false }).isValid;
}
